import React, { useEffect, useRef, useState } from "react";
import { PDFDocument } from "pdf-lib";
import SignaturePad from "signature_pad";
import "./ContratoTab.css";

const normalizeSpecialtyCode = (value) => {
  const code = String(value || "").trim().toLowerCase();

  if (code === "logopeda") {
    return "logopedia";
  }

  return code;
};

const getSpecialtyLabel = (code) => {
  const labels = {
    psicologia: "Psicología",
    logopedia: "Logopedia",
    nutricion: "Nutrición",
    administracion: "Administración",
  };

  return labels[normalizeSpecialtyCode(code)] || code;
};

export default function ConsentimientoMenoresTab({ paciente }) {
  const canvasRef = useRef(null);

  const [signaturePad, setSignaturePad] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [selectedSpec, setSelectedSpec] = useState("");
  const [availableSpecs, setAvailableSpecs] = useState([]);

  // Cargar y normalizar las especialidades del paciente.
  useEffect(() => {
    if (!paciente?.servicios) {
      setAvailableSpecs([]);
      setSelectedSpec("");
      return;
    }

    const rawServices = Array.isArray(paciente.servicios)
      ? paciente.servicios
      : String(paciente.servicios)
          .split(",")
          .map((service) => service.trim())
          .filter(Boolean);

    const normalizedServices = [
      ...new Set(
        rawServices
          .map((service) => {
            if (typeof service === "object" && service !== null) {
              return normalizeSpecialtyCode(
                service.code ||
                  service.specialty_code ||
                  service.specialty ||
                  service.nombre,
              );
            }

            return normalizeSpecialtyCode(service);
          })
          .filter(Boolean),
      ),
    ];

    setAvailableSpecs(normalizedServices);

    setSelectedSpec((current) => {
      const normalizedCurrent = normalizeSpecialtyCode(current);

      if (
        normalizedCurrent &&
        normalizedServices.includes(normalizedCurrent)
      ) {
        return normalizedCurrent;
      }

      return normalizedServices.length === 1
        ? normalizedServices[0]
        : "";
    });
  }, [paciente]);

  // Inicializar la firma.
  useEffect(() => {
    if (!canvasRef.current) return undefined;

    const canvas = canvasRef.current;
    canvas.width = 350;
    canvas.height = 150;

    const pad = new SignaturePad(canvas, {
      backgroundColor: "#fff",
      penColor: "#000",
    });

    setSignaturePad(pad);

    return () => {
      pad.off();
    };
  }, [fileUrl]);

  // Obtener el consentimiento PDF.
  useEffect(() => {
    if (!paciente?.id || !selectedSpec) return;

    const specialty = normalizeSpecialtyCode(selectedSpec);

    setLoading(true);
    setError("");
    setMessage("");
    setUploadedFile(null);
    setFileUrl(null);

    const params = new URLSearchParams({
      id: String(paciente.id),
      specialty,
    });

    fetch(
      `https://te-ayudo.es/back/api/generate_consent.php?${params.toString()}`,
    )
      .then(async (response) => {
        const text = await response.text();

        let data;

        try {
          data = JSON.parse(text);
        } catch {
          throw new Error(
            "La respuesta del servidor no es un JSON válido.",
          );
        }

        if (!response.ok || !data.ok) {
          throw new Error(
            data.error || "Error al generar el consentimiento.",
          );
        }

        return data;
      })
      .then((data) => {
        if (!data.pdf_local) {
          throw new Error(
            "El servidor no ha devuelto el PDF del consentimiento.",
          );
        }

        setFileUrl(data.pdf_local);
      })
      .catch((err) => {
        setError(
          err.message || "No se pudo generar el consentimiento.",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, [paciente?.id, selectedSpec]);

  const handleClear = () => {
    signaturePad?.clear();
  };

  const handleSave = async () => {
    if (!signaturePad || signaturePad.isEmpty()) {
      alert("Por favor, firma antes de guardar.");
      return;
    }

    if (!fileUrl) {
      alert("No se ha podido cargar el consentimiento.");
      return;
    }

    const specialty = normalizeSpecialtyCode(selectedSpec);

    try {
      setUploading(true);
      setMessage("");

      // 1. Cargar y firmar el PDF.
      const pdfResponse = await fetch(fileUrl);

      if (!pdfResponse.ok) {
        throw new Error(
          "No se ha podido descargar el consentimiento para firmarlo.",
        );
      }

      const pdfBytes = await pdfResponse.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      const signatureResponse = await fetch(
        signaturePad.toDataURL(),
      );
      const pngBytes = await signatureResponse.arrayBuffer();
      const pngImage = await pdfDoc.embedPng(pngBytes);

      const pages = pdfDoc.getPages();
      const page = pages[pages.length - 1];
      const { width } = page.getSize();

      const marginLeft = width - 260;
      const marginBottom = 70;
      const boxWidth = 220;
      const boxHeight = 90;

      const pngDimensions = pngImage.scale(1);
      const scale = Math.min(
        boxWidth / pngDimensions.width,
        boxHeight / pngDimensions.height,
      );

      page.drawImage(pngImage, {
        x: marginLeft,
        y: marginBottom,
        width: pngDimensions.width * scale,
        height: pngDimensions.height * scale,
      });

      const finalPdf = await pdfDoc.save();
      const blob = new Blob([finalPdf], {
        type: "application/pdf",
      });

      // 2. Subir a Drive.
      const uploadFormData = new FormData();

      uploadFormData.append(
        "file",
        blob,
        `consentimiento_firmado_${paciente.id}_${specialty}.pdf`,
      );
      uploadFormData.append("patient_id", String(paciente.id));
      uploadFormData.append("type", "consentimiento");
      uploadFormData.append("specialty", specialty);

      const uploadResponse = await fetch(
        "https://te-ayudo.es/back/api/upload_drive.php",
        {
          method: "POST",
          body: uploadFormData,
        },
      );

      const uploadData = await uploadResponse.json();

      if (!uploadResponse.ok || !uploadData.ok) {
        throw new Error(
          uploadData.error ||
            "No se pudo subir el consentimiento firmado.",
        );
      }

      setUploadedFile({
        webViewLink: uploadData.file?.webViewLink || null,
        pdf_local: uploadData.pdf_local || null,
      });

      // 3. Enviar el PDF firmado por correo.
      // Se crea un FormData nuevo porque el anterior ya fue consumido.
      const mailFormData = new FormData();

      mailFormData.append(
        "file",
        blob,
        `consentimiento_firmado_${paciente.id}_${specialty}.pdf`,
      );
      mailFormData.append("patient_id", String(paciente.id));
      mailFormData.append("type", "consentimiento");
      mailFormData.append("specialty", specialty);

      const mailResponse = await fetch(
        "https://te-ayudo.es/back/api/send_consent_mail.php",
        {
          method: "POST",
          body: mailFormData,
        },
      );

      const mailData = await mailResponse.json();

      if (mailResponse.ok && mailData.ok) {
        setMessage(
          "✅ Consentimiento firmado, subido y enviado por correo correctamente.",
        );
      } else {
        setMessage(
          `⚠️ Consentimiento subido, pero no se pudo enviar el correo: ${
            mailData.error || "error desconocido"
          }`,
        );
      }
    } catch (err) {
      setMessage(
        `❌ ${err.message || "No se pudo guardar el consentimiento."}`,
      );
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="contrato-tab">
      <h3>🧾 Consentimiento expreso de menores</h3>

      {availableSpecs.length > 0 && (
        <div className="form-row">
          <label htmlFor="consent-specialty">
            Especialidad / Servicio
          </label>

          <select
            id="consent-specialty"
            value={selectedSpec}
            onChange={(event) =>
              setSelectedSpec(
                normalizeSpecialtyCode(event.target.value),
              )
            }
          >
            <option value="">
              -- Selecciona un servicio --
            </option>

            {availableSpecs.map((specialty) => (
              <option key={specialty} value={specialty}>
                {getSpecialtyLabel(specialty)}
              </option>
            ))}
          </select>
        </div>
      )}

      {availableSpecs.length === 0 && (
        <p>
          Este paciente no tiene especialidades asignadas.
        </p>
      )}

      {loading && <p>Generando consentimiento...</p>}

      {error && (
        <p style={{ color: "red" }}>
          ❌ {error}
        </p>
      )}

      {!loading && fileUrl && (
        <iframe
          src={fileUrl}
          className="contrato-preview"
          title="Vista previa del consentimiento"
          style={{
            width: "100%",
            height: "800px",
            border: "none",
          }}
        />
      )}

      {fileUrl && (
        <div className="signature-section">
          <h4>✍️ Firma del tutor legal</h4>

          <div className="signature-box">
            <canvas
              ref={canvasRef}
              width={350}
              height={150}
              className="signature-canvas"
            />
          </div>

          <div className="signature-actions">
            <button type="button" onClick={handleClear}>
              Borrar firma
            </button>

            <button
              type="button"
              onClick={handleSave}
              disabled={uploading}
            >
              {uploading
                ? "Guardando..."
                : "Guardar consentimiento firmado"}
            </button>
          </div>
        </div>
      )}

      {message && (
        <div className="message-box">
          <p>{message}</p>

          {uploadedFile && (
            <button
              type="button"
              className="btn-ver-contrato"
              onClick={() => {
                const url =
                  uploadedFile.pdf_local ||
                  uploadedFile.webViewLink;

                if (url) {
                  window.open(
                    url,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }
              }}
            >
              📄 Ver consentimiento firmado
            </button>
          )}
        </div>
      )}
    </div>
  );
}